import type { KeyPerson } from './types';

/**
 * Flatten the tolerant `StoryContext.keyPeople` union (bare strings and/or
 * `{ name, role? }` objects) into a canonical `KeyPerson[]` for display. Mirrors
 * the backend `normalizeKeyPeople` contract: trims, drops blank-name / empty-role
 * entries, and dedupes case-insensitively by name (keeping the first role).
 */
export function normalizeKeyPeople(
  input: Array<string | KeyPerson> | null | undefined,
): KeyPerson[] {
  if (!Array.isArray(input)) return [];

  const out: KeyPerson[] = [];
  const indexByName = new Map<string, number>();

  for (const entry of input) {
    let name = '';
    let role: string | undefined;

    if (typeof entry === 'string') {
      name = entry.trim();
    } else if (entry && typeof entry === 'object') {
      name = typeof entry.name === 'string' ? entry.name.trim() : '';
      const rawRole = typeof entry.role === 'string' ? entry.role.trim() : '';
      role = rawRole.length > 0 ? rawRole : undefined;
    } else {
      continue;
    }

    if (!name) continue;

    const key = name.toLowerCase();
    const existingIdx = indexByName.get(key);
    if (existingIdx !== undefined) {
      if (!out[existingIdx].role && role) out[existingIdx].role = role;
      continue;
    }

    indexByName.set(key, out.length);
    out.push(role ? { name, role } : { name });
  }

  return out;
}

/** Render a key person as `name · role` or just the name. */
export function formatKeyPerson(person: KeyPerson): string {
  return person.role ? `${person.name} · ${person.role}` : person.name;
}
